import { useMemo } from 'react';
import { store } from 'index';
import { words } from 'utils/dictionary';
import {
  objType,
  teacherUrlStateType
} from 'types';
import { LOG_OUT_SAGA } from 'actions';
import { SET_MODAL_STATE } from 'actions/modalActions';

const useButtonProps = (
  classes:         objType,
  variant:         string,
  lessonNumber:    string,
  email:           string,
  name:            string,
  message:         string,
  teacherUrlState: teacherUrlStateType,
  setError:        (val: string) => void,
  closeModal:      () => void
) => {
  const state     = store.getState();
  const lang      = state.components.lang;
  const manualVal = state.manualNav.manualVal;
  const student   = state.data.userInfo.userRole === 'student';

  const buttonProps = useMemo(() => {
    const logOut       = () => { store.dispatch(LOG_OUT_SAGA()); };
    const showUserInfo = () => {
      store.dispatch(SET_MODAL_STATE({ modalVariant: 'showUserInfo', showModal: true }));
    };

    const copyUrl = () => {
      import('functions/actions/copy_url').then(func => {
        func.copyUrl(teacherUrlState);
      });
      import('functions/modal/close_modal').then(openModal => {
        openModal.handleCloseModal();
      });
    };

    const joinLesson = () => {
      import('../functions/joinLesson').then(func => {
        func.joinLesson(lessonNumber, setError);
      });
    };

    const submitMessage = () => {
      import('functions/actions/handle_submit_message').then(func => {
        func.handleSubmitMessage(email, name, message, setError);
      });
    };

    const chatWithGali = () => {
      import('functions/actions/handle_submit_message').then(() => {
        window.open('http://wa.me/972508386502', '');
      });
    };

    const closeModalText = ['logOut', 'contact us', 'getPlus', 'joinLesson', 'followLinkNotFound'].includes(variant)
      ? words['return'][lang]
      : words['ok'][lang];

    const disableWhastappReminder = () => {
      import('../functions/handleWhastappReminder').then(func => {
        func.handleWhastappReminder(true, closeModal);
      });
      window.open('https://chat.whatsapp.com/Hq65bXyvLpMKB856EAkIo1');
    };

    const updateWhastappReminder = () => {
      import('../functions/handleWhastappReminder').then(func => {
        func.handleWhastappReminder(false, closeModal);
      });
    };

    const obj: { [key: string]: { [key: string]: any } } = {
      close:               { txt: closeModalText,              func: closeModal,     class: classes.button, manualVal },
      userNotLoggedIn:     { txt: words['signIn2'][lang],      func: logOut,         class: classes.button, manualVal },
      logOut:              { txt: words['signOut'][lang],      func: logOut,         class: classes.button, manualVal },
      freezModal:          { txt: words['signOut'][lang],      func: logOut,         class: classes.button, manualVal },
      freeAccessForAll:    { txt: words['signOut'][lang],      func: logOut,         class: classes.button, manualVal },
      showUserInfo:        { txt: words['signOut'][lang],      func: logOut,         class: classes.button, manualVal },
      teacherUrl:          { txt: words['ok'][lang],           func: copyUrl,        class: classes.button, manualVal },
      joinLesson:          { txt: words['ok'][lang],           func: joinLesson,     class: classes.button, manualVal },
      followLinkNotFound:  { txt: words['ok'][lang],           func: joinLesson,     class: classes.button, manualVal },
      chatWithGali:        { txt: words['chatWithGali'][lang], func: chatWithGali,   class: classes.button, manualVal },
      guestModal:          { txt: words['chatWithGali'][lang], func: chatWithGali,   class: classes.button, manualVal },
      'contact us':        { txt: words['sendMessage'][lang],  func: submitMessage,  class: classes.button, manualVal },
      getPlus:             { txt: words['sendMessage'][lang],  func: submitMessage,  class: classes.button, manualVal },
      subscriptionExpired: [
        { txt: words['showDetails'][lang], func: showUserInfo,  class: classes.button, manualVal },
        { txt: words['sendMessage'][lang], func: submitMessage, class: classes.button, manualVal },
        { txt: words['signOut'][lang],     func: logOut,        class: classes.button, manualVal },
      ],
      whatsappRegistration: [
        { txt: words['whatsappRegister'][lang], func: disableWhastappReminder, class: classes.button, manualVal },
        { txt: words['remindMeNextWeek'][lang], func: updateWhastappReminder,  class: classes.button, manualVal },
      ],
    };

    if (['teacherWarningModal', 'teacherUrl', 'freezModal', 'showUserInfo'].includes(variant)) {
      return [obj[variant]];
    }

    if (variant === 'whatsappRegistration') {
      return [obj[variant][0], obj[variant][1]];
    }

    if (variant === 'subscriptionExpired' && !student) {
      return [obj[variant][0], obj[variant][1], obj[variant][2]];
    }

    if (variant === 'contact us' || variant === 'getPlus') {
      if (student) {
        return [obj['close']];
      } else {
        return [obj[variant], obj['close'], obj['chatWithGali']];
      }
    }

    if (variant === 'subscriptionExpired' && student) {
      return [obj['logOut']];
    }

    // default
    return obj[variant] ? [obj[variant], obj['close']] : [obj['close']];
  }, [
    variant,
    classes,
    lessonNumber,
    email,
    name,
    message,
    teacherUrlState,
    lang,
    manualVal,
    student,
    setError,
    closeModal
  ]);

  // --- NEW: hide the "send" button for WhatsApp-style variants ---
  const filtered = useMemo(
    () => {
      const HIDE_SEND_VARIANTS = ['contact us', 'getPlus', 'subscriptionExpired'];
      if (!HIDE_SEND_VARIANTS.includes(variant)) return buttonProps;

      // works for both single-button objects and arrays (subscriptionExpired)
      return buttonProps.filter(b => b && b['txt'] !== words['sendMessage'][lang]);
    },
    [buttonProps, variant, lang]
  );

  return filtered;
};

export default useButtonProps;
